import { AlertTriangle, Info, X } from 'lucide-react';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Dialog as AriaDialog,
  Button as AriaButton,
  Heading,
  Modal as AriaModal,
  ModalOverlay,
} from 'react-aria-components';
import { Button } from './button';
import { cx } from './styles';

const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-6xl' };

interface ModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly title: string;
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: keyof typeof sizes;
  readonly role?: 'dialog' | 'alertdialog';
  readonly icon?: ReactNode;
  readonly isDismissable?: boolean;
  readonly dirty?: boolean;
}

interface DialogGuard {
  readonly reportChanges: (changed: boolean) => void;
  readonly requestClose: () => void;
}

const DialogGuardContext = createContext<DialogGuard | null>(null);

const DISCARD_CHANGES: ConfirmOptions = {
  title: 'Wijzigingen weggooien?',
  message: 'Wat je in dit venster hebt ingevuld, wordt niet bewaard.',
  confirmLabel: 'Weggooien',
  cancelLabel: 'Verder bewerken',
};

export function useDialogChanges(changed: boolean): void {
  const report = use(DialogGuardContext)?.reportChanges;
  useEffect(() => {
    report?.(changed);
  }, [report, changed]);
  useEffect(
    () => () => {
      report?.(false);
    },
    [report],
  );
}

export function DialogCancel({ children = 'Annuleren' }: { readonly children?: ReactNode }) {
  const guard = use(DialogGuardContext);
  return (
    <Button
      variant="secondary"
      onClick={() => {
        guard?.requestClose();
      }}
    >
      {children}
    </Button>
  );
}

export function Modal({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  icon,
  isDismissable = true,
  dirty = false,
}: ModalProps) {
  const confirm = useConfirm();
  const [childChanges, setChildChanges] = useState(false);
  const changed = isOpen && (dirty || childChanges);

  const requestClose = useCallback(() => {
    if (!changed) {
      onOpenChange(false);
      return;
    }
    void confirm(DISCARD_CHANGES).then((discard) => {
      if (discard) {
        setChildChanges(false);
        onOpenChange(false);
      }
    });
  }, [changed, confirm, onOpenChange]);

  const guard = useMemo<DialogGuard>(
    () => ({ reportChanges: setChildChanges, requestClose }),
    [requestClose],
  );

  useEffect(() => {
    if (!changed) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [changed]);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (open) {
          onOpenChange(true);
        } else {
          requestClose();
        }
      }}
      isDismissable={isDismissable}
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-[2px] data-entering:animate-fade-in data-exiting:animate-fade-out sm:items-center sm:p-4"
    >
      <AriaModal
        className={cx(
          'flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-slate-900/5 max-sm:data-entering:animate-sheet-in max-sm:data-exiting:animate-sheet-out sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:data-entering:animate-pop-in sm:data-exiting:animate-pop-out',
          sizes[size],
        )}
      >
        <AriaDialog role={role} className="relative flex min-h-0 flex-1 flex-col outline-none">
          <DialogGuardContext value={guard}>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="flex gap-4 px-5 pt-6 pb-5 sm:px-6">
                {icon ? <div className="shrink-0">{icon}</div> : null}
                <div className="min-w-0 flex-1">
                  <Heading
                    slot="title"
                    className="pr-10 font-display text-lg font-semibold text-slate-900"
                  >
                    {title}
                  </Heading>
                  {description ? (
                    <div className="mt-1.5 text-sm leading-relaxed text-slate-600">
                      {description}
                    </div>
                  ) : null}
                  {children ? <div className="mt-4">{children}</div> : null}
                </div>
              </div>
            </div>
            {footer ? (
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6 sm:pb-4">
                {footer}
              </div>
            ) : (
              <div
                aria-hidden="true"
                className="h-[env(safe-area-inset-bottom)] shrink-0 sm:hidden"
              />
            )}
            {isDismissable ? (
              <AriaButton
                slot="close"
                aria-label="Sluiten"
                className="absolute top-4 right-4 flex size-8 items-center justify-center rounded-lg bg-white/90 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 data-focus-visible:ring-2 data-focus-visible:ring-brand-600 pointer-coarse:top-3 pointer-coarse:right-3 pointer-coarse:size-10"
              >
                <X aria-hidden="true" className="size-4" />
              </AriaButton>
            ) : null}
          </DialogGuardContext>
        </AriaDialog>
      </AriaModal>
    </ModalOverlay>
  );
}

export function DialogActions({ children }: { readonly children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-5 -mb-5 mt-6 flex flex-col-reverse gap-2 border-t border-slate-100 bg-white px-5 py-4 sm:static sm:mx-0 sm:mb-0 sm:flex-row sm:justify-end sm:bg-transparent sm:px-0 sm:pt-5 sm:pb-0">
      {children}
    </div>
  );
}

export interface ConfirmOptions {
  readonly title: string;
  readonly message?: ReactNode;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: 'danger' | 'primary';
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

interface PendingConfirm {
  readonly options: ConfirmOptions;
  readonly resolve: (confirmed: boolean) => void;
}

export function ConfirmProvider({ children }: { readonly children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ options, resolve });
      }),
    [],
  );

  const settle = (confirmed: boolean) => {
    pending?.resolve(confirmed);
    setPending(null);
  };

  const options = pending?.options;
  const danger = options?.tone !== 'primary';

  return (
    <ConfirmContext value={confirm}>
      {children}
      <Modal
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            settle(false);
          }
        }}
        role="alertdialog"
        size="sm"
        title={options?.title ?? ''}
        description={options?.message}
        icon={
          danger ? (
            <span className="flex size-10 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle aria-hidden="true" className="size-5" />
            </span>
          ) : (
            <span className="flex size-10 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Info aria-hidden="true" className="size-5" />
            </span>
          )
        }
        footer={
          <>
            <Button
              variant="secondary"
              autoFocus={danger}
              onClick={() => {
                settle(false);
              }}
            >
              {options?.cancelLabel ?? 'Annuleren'}
            </Button>
            <Button
              variant={danger ? 'dangerSolid' : 'primary'}
              autoFocus={!danger}
              onClick={() => {
                settle(true);
              }}
            >
              {options?.confirmLabel ?? 'Doorgaan'}
            </Button>
          </>
        }
      />
    </ConfirmContext>
  );
}

export function useConfirm(): Confirm {
  const confirm = use(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm needs a ConfirmProvider');
  }
  return confirm;
}
